import { inject, Injectable } from '@angular/core';
import { Observable, shareReplay, tap } from 'rxjs';
import {
  BackendService,
  HttpOptions,
  PaginatedResponseModel,
} from '@ipg/ngx-frontend-toolset';

import { isEqual } from 'date-fns';
import { Client, ClientNameRef } from '../../../models/clients.model';
import {
  ClientMoveRequest,
  MovePreviewResponse,
} from '../models/move-client.models';
import {
  ResourceCreate,
  ResourceGetAll,
  ResourceGetById,
  ResourceUpdate,
} from '../../../models/common/resource.model';

interface CachedEntry {
  stream: Observable<Client>;
  updatedAt: Date; // or number
}

@Injectable({
  providedIn: 'root',
})
export class ClientsService
  implements ResourceCreate, ResourceUpdate, ResourceGetById, ResourceGetAll
{
  // ResourceDelete,
  private readonly _backend = inject(BackendService);
  private readonly _cache = new Map<string, CachedEntry>();

  getById(id: string, options?: HttpOptions): Observable<Client> {
    return this._backend.get<Client>(`/clients/${id}`, options).pipe(
      tap((c) => {
        this._checkAndForceCacheToUpdateSync(c);
      })
    );
  }

  /**
   * Resolve a set of client ids to lightweight name references. The backend
   * scopes the result to the caller's unit and omits ids it may not view, so
   * the response only contains clients the user is allowed to see.
   */
  getByIds(ids: string[], options?: HttpOptions): Observable<ClientNameRef[]> {
    return this._backend.get<ClientNameRef[]>('/clients/by-ids', {
      ...options,
      params: { ...options?.params, ids },
    });
  }

  getAll(options?: HttpOptions): Observable<PaginatedResponseModel<Client[]>> {
    return this._backend
      .get<PaginatedResponseModel<Client[]>>(`/clients`, options)
      .pipe(
        tap((resp) => {
          const { items } = resp;
          for (const c of items) this._checkAndForceCacheToUpdateSync(c);
        })
      );
  }

  create(data: unknown, options?: HttpOptions): Observable<unknown> {
    return this._backend.post(`/clients`, data, options);
  }

  update(id: string, data: unknown, options?: HttpOptions): Observable<unknown> {
    return this._backend
      .put(`/clients/${id}`, data, options)
      .pipe(tap(() => this.invalidateCachedClient(id)));
  }

  delete(id: string, options?: HttpOptions): Observable<unknown> {
    return this._backend
      .delete(`/clients/${id}`, options)
      .pipe(tap(() => this.invalidateCachedClient(id)));
  }

  restore(id: string) {
    return this._backend.put(`/clients/${id}/restore`, null);
  }

  getCachedClient(id: string, options?: HttpOptions): Observable<Client> {
    const hit = this._cache.get(id);
    if (hit) return hit.stream;

    const stream = this.getById(id, options).pipe(shareReplay(1));
    // Prime updatedAt once the HTTP completes (optional)
    stream.subscribe({
      next: (c) => this._cache.set(id, { stream, updatedAt: new Date(c.updatedAt) }),
      error: () => this._cache.delete(id),
    });

    return stream;
  }

  invalidateCachedClient(id: string): void {
    this._cache.delete(id);
  }

  getMovePreview(clientId: string, targetUnitId: string): Observable<MovePreviewResponse> {
    return this._backend.get<MovePreviewResponse>(
      `/clients/${clientId}/move/preview`,
      { params: { targetUnitId } }
    );
  }

  moveClient(clientId: string, data: ClientMoveRequest): Observable<string> {
    return this._backend
      .post<string>(`/clients/${clientId}/move`, data)
      .pipe(tap(() => this.invalidateCachedClient(clientId)));
  }

  private _checkAndForceCacheToUpdateSync(client: Client): void {
    const hit = this._cache.get(client._id);
    if (!hit) return;
    if (!isEqual(hit.updatedAt, new Date(client.updatedAt))) {
      this.invalidateCachedClient(client._id);
    }
  }
}
